import type { SkinRarity } from './weapon-skin-types';

// ---------------------------------------------------------------------------
// Sticker System — Types, slot configs, and starter manifest
// ---------------------------------------------------------------------------

/** A sticker definition in the catalog */
export interface StickerDefinition {
  /** Unique sticker ID (50000+ range) */
  id: number;
  /** Display name */
  name: string;
  /** Collection (e.g. "Katowice 2024", "Community Stickers") */
  collection: string;
  /** Rarity tier */
  rarity: SkinRarity;
  /** Path to sticker texture (PNG with alpha, 512x512) */
  texturePath: string;
  /** Whether this sticker has holographic shimmer effect */
  holographic: boolean;
  /** Whether this sticker has metallic foil variant */
  foil: boolean;
  /** Whether this sticker has glitter particle effect */
  glitter: boolean;
  /** Thumbnail for UI (128x128 webp) */
  thumbnailPath: string;
  /** Description / flavor text */
  description?: string;
}

/** A sticker applied to a specific weapon skin instance */
export interface StickerInstance {
  /** References StickerDefinition.id */
  stickerId: number;
  /** Slot position on weapon (0-4) */
  slot: number;
  /** Sticker wear (0.0 = pristine, 1.0 = fully scraped) */
  wear: number;
  /** UV offset X adjustment */
  offsetX: number;
  /** UV offset Y adjustment */
  offsetY: number;
  /** Rotation in degrees */
  rotation: number;
}

/** Name tag applied to a weapon */
export interface NameTag {
  /** Custom name text (max 20 chars) */
  text: string;
  /** Color hex for the name tag text */
  color: number;
}

/** Predefined sticker slot positions per weapon type (UV coordinates) */
export interface StickerSlotConfig {
  /** World-space position offset from weapon origin */
  position: [number, number, number];
  /** Euler rotation [x, y, z] */
  rotation: [number, number, number];
  /** Scale of the sticker decal */
  scale: number;
}

// ---------------------------------------------------------------------------
// Sticker slot configurations per weapon
// ---------------------------------------------------------------------------

/** Sticker slot configurations per weapon */
export const STICKER_SLOTS: Record<string, StickerSlotConfig[]> = {
  // Rifles: barrel, body-front, body-back, magazine, stock
  ak47: [
    { position: [0, 0.045, -0.30], rotation: [0, 0, 0], scale: 0.04 },
    { position: [0, 0.045, -0.15], rotation: [0, 0, 0], scale: 0.04 },
    { position: [0, 0.045, -0.05], rotation: [0, 0, 0], scale: 0.04 },
    { position: [0.01, 0.02, -0.10], rotation: [0, 0, Math.PI / 6], scale: 0.03 },
    { position: [0, 0.045, 0.05], rotation: [0, 0, 0], scale: 0.035 },
  ],
  m4a4: [
    { position: [0, 0.045, -0.30], rotation: [0, 0, 0], scale: 0.04 },
    { position: [0, 0.045, -0.15], rotation: [0, 0, 0], scale: 0.04 },
    { position: [0, 0.045, -0.05], rotation: [0, 0, 0], scale: 0.04 },
    { position: [0.01, 0.02, -0.10], rotation: [0, 0, Math.PI / 6], scale: 0.03 },
    { position: [0, 0.045, 0.05], rotation: [0, 0, 0], scale: 0.035 },
  ],
  awp: [
    { position: [0, 0.04, -0.35], rotation: [0, 0, 0], scale: 0.04 },
    { position: [0, 0.04, -0.20], rotation: [0, 0, 0], scale: 0.04 },
    { position: [0, 0.04, -0.05], rotation: [0, 0, 0], scale: 0.04 },
    { position: [0, 0.04, 0.05], rotation: [0, 0, 0], scale: 0.035 },
  ],
  // Pistols: slide, grip-left, grip-right, barrel
  glock: [
    { position: [0, 0.035, -0.06], rotation: [0, 0, 0], scale: 0.025 },
    { position: [0.015, 0.025, -0.03], rotation: [0, Math.PI / 2, 0], scale: 0.02 },
    { position: [-0.015, 0.025, -0.03], rotation: [0, -Math.PI / 2, 0], scale: 0.02 },
    { position: [0, 0.035, -0.10], rotation: [0, 0, 0], scale: 0.02 },
  ],
  deagle: [
    { position: [0, 0.038, -0.08], rotation: [0, 0, 0], scale: 0.03 },
    { position: [0.018, 0.028, -0.05], rotation: [0, Math.PI / 2, 0], scale: 0.025 },
    { position: [-0.018, 0.028, -0.05], rotation: [0, -Math.PI / 2, 0], scale: 0.025 },
    { position: [0, 0.038, -0.14], rotation: [0, 0, 0], scale: 0.025 },
  ],
  // Default for weapons without specific configs
  _default: [
    { position: [0, 0.04, -0.20], rotation: [0, 0, 0], scale: 0.035 },
    { position: [0, 0.04, -0.10], rotation: [0, 0, 0], scale: 0.035 },
    { position: [0, 0.04, 0.0], rotation: [0, 0, 0], scale: 0.035 },
    { position: [0, 0.04, 0.08], rotation: [0, 0, 0], scale: 0.03 },
  ],
};

/** Get sticker slots for a weapon, falling back to defaults */
export function getStickerSlots(weaponId: string): StickerSlotConfig[] {
  return STICKER_SLOTS[weaponId] ?? STICKER_SLOTS._default;
}

/** Maximum stickers per weapon */
export const MAX_STICKERS_PER_WEAPON = 5;

/** Maximum name tag length */
export const MAX_NAME_TAG_LENGTH = 20;

// ---------------------------------------------------------------------------
// Starter sticker manifest (~20 stickers across collections & rarities)
// ID range: 50000+
// ---------------------------------------------------------------------------

export const STICKER_MANIFEST: ReadonlyMap<number, StickerDefinition> = new Map([
  // ── Katowice 2024 (team stickers) ────────────────────────────────────
  [50000, {
    id: 50000,
    name: 'Navi (Holo)',
    collection: 'Katowice 2024',
    rarity: 'classified',
    texturePath: 'stickers/katowice24/navi-holo.png',
    holographic: true,
    foil: false,
    glitter: false,
    thumbnailPath: 'thumbs/stickers/katowice24/navi-holo.webp',
    description: 'Natus Vincere holographic team sticker.',
  }],
  [50001, {
    id: 50001,
    name: 'FaZe Clan',
    collection: 'Katowice 2024',
    rarity: 'mil-spec',
    texturePath: 'stickers/katowice24/faze.png',
    holographic: false,
    foil: false,
    glitter: false,
    thumbnailPath: 'thumbs/stickers/katowice24/faze.webp',
    description: 'FaZe Clan team logo sticker.',
  }],
  [50002, {
    id: 50002,
    name: 'Cloud9 (Foil)',
    collection: 'Katowice 2024',
    rarity: 'restricted',
    texturePath: 'stickers/katowice24/cloud9-foil.png',
    holographic: false,
    foil: true,
    glitter: false,
    thumbnailPath: 'thumbs/stickers/katowice24/cloud9-foil.webp',
    description: 'Cloud9 metallic foil team sticker.',
  }],
  [50003, {
    id: 50003,
    name: 'G2 Esports (Gold)',
    collection: 'Katowice 2024',
    rarity: 'covert',
    texturePath: 'stickers/katowice24/g2-gold.png',
    holographic: true,
    foil: true,
    glitter: false,
    thumbnailPath: 'thumbs/stickers/katowice24/g2-gold.webp',
    description: 'G2 Esports gold foil edition. Extremely rare.',
  }],
  [50004, {
    id: 50004,
    name: 'Vitality',
    collection: 'Katowice 2024',
    rarity: 'consumer',
    texturePath: 'stickers/katowice24/vitality.png',
    holographic: false,
    foil: false,
    glitter: false,
    thumbnailPath: 'thumbs/stickers/katowice24/vitality.webp',
    description: 'Team Vitality logo sticker.',
  }],

  // ── Community Stickers ────────────────────────────────────────────────
  [50100, {
    id: 50100,
    name: 'Headshot Guarantee',
    collection: 'Community Stickers',
    rarity: 'industrial',
    texturePath: 'stickers/community/headshot-guarantee.png',
    holographic: false,
    foil: false,
    glitter: false,
    thumbnailPath: 'thumbs/stickers/community/headshot-guarantee.webp',
    description: 'Headshots or your money back.',
  }],
  [50101, {
    id: 50101,
    name: 'Easy Peasy',
    collection: 'Community Stickers',
    rarity: 'consumer',
    texturePath: 'stickers/community/easy-peasy.png',
    holographic: false,
    foil: false,
    glitter: false,
    thumbnailPath: 'thumbs/stickers/community/easy-peasy.webp',
    description: 'Lemon squeezy.',
  }],
  [50102, {
    id: 50102,
    name: 'Clutch King (Holo)',
    collection: 'Community Stickers',
    rarity: 'classified',
    texturePath: 'stickers/community/clutch-king-holo.png',
    holographic: true,
    foil: false,
    glitter: false,
    thumbnailPath: 'thumbs/stickers/community/clutch-king-holo.webp',
    description: 'Crown yourself the clutch king.',
  }],
  [50103, {
    id: 50103,
    name: 'Bombsite A',
    collection: 'Community Stickers',
    rarity: 'consumer',
    texturePath: 'stickers/community/bombsite-a.png',
    holographic: false,
    foil: false,
    glitter: false,
    thumbnailPath: 'thumbs/stickers/community/bombsite-a.webp',
    description: 'Rush A. Don\'t stop.',
  }],
  [50104, {
    id: 50104,
    name: 'Bombsite B',
    collection: 'Community Stickers',
    rarity: 'consumer',
    texturePath: 'stickers/community/bombsite-b.png',
    holographic: false,
    foil: false,
    glitter: false,
    thumbnailPath: 'thumbs/stickers/community/bombsite-b.webp',
    description: 'B rush no stop.',
  }],
  [50105, {
    id: 50105,
    name: 'Glitter Bomb',
    collection: 'Community Stickers',
    rarity: 'restricted',
    texturePath: 'stickers/community/glitter-bomb.png',
    holographic: false,
    foil: false,
    glitter: true,
    thumbnailPath: 'thumbs/stickers/community/glitter-bomb.webp',
    description: 'Sparkle and shine on every frag.',
  }],

  // ── Crypto Collection ─────────────────────────────────────────────────
  [50200, {
    id: 50200,
    name: 'Diamond Hands',
    collection: 'Crypto Collection',
    rarity: 'restricted',
    texturePath: 'stickers/crypto/diamond-hands.png',
    holographic: false,
    foil: true,
    glitter: false,
    thumbnailPath: 'thumbs/stickers/crypto/diamond-hands.webp',
    description: 'Never selling, never folding.',
  }],
  [50201, {
    id: 50201,
    name: 'To The Moon',
    collection: 'Crypto Collection',
    rarity: 'mil-spec',
    texturePath: 'stickers/crypto/to-the-moon.png',
    holographic: false,
    foil: false,
    glitter: false,
    thumbnailPath: 'thumbs/stickers/crypto/to-the-moon.webp',
    description: 'Rocket emoji not included.',
  }],
  [50202, {
    id: 50202,
    name: 'HODL (Holo)',
    collection: 'Crypto Collection',
    rarity: 'classified',
    texturePath: 'stickers/crypto/hodl-holo.png',
    holographic: true,
    foil: false,
    glitter: false,
    thumbnailPath: 'thumbs/stickers/crypto/hodl-holo.webp',
    description: 'Hold on for dear life — in holographic.',
  }],
  [50203, {
    id: 50203,
    name: 'Rug Pull',
    collection: 'Crypto Collection',
    rarity: 'industrial',
    texturePath: 'stickers/crypto/rug-pull.png',
    holographic: false,
    foil: false,
    glitter: false,
    thumbnailPath: 'thumbs/stickers/crypto/rug-pull.webp',
    description: 'Trust no one.',
  }],

  // ── Skill Icons Collection ────────────────────────────────────────────
  [50300, {
    id: 50300,
    name: 'Crosshair',
    collection: 'Skill Icons',
    rarity: 'consumer',
    texturePath: 'stickers/icons/crosshair.png',
    holographic: false,
    foil: false,
    glitter: false,
    thumbnailPath: 'thumbs/stickers/icons/crosshair.webp',
    description: 'Stay on target.',
  }],
  [50301, {
    id: 50301,
    name: 'Ace Star (Glitter)',
    collection: 'Skill Icons',
    rarity: 'restricted',
    texturePath: 'stickers/icons/ace-star-glitter.png',
    holographic: false,
    foil: false,
    glitter: true,
    thumbnailPath: 'thumbs/stickers/icons/ace-star-glitter.webp',
    description: 'Five kills. One round. All glory.',
  }],
  [50302, {
    id: 50302,
    name: 'Knife Kill',
    collection: 'Skill Icons',
    rarity: 'mil-spec',
    texturePath: 'stickers/icons/knife-kill.png',
    holographic: false,
    foil: false,
    glitter: false,
    thumbnailPath: 'thumbs/stickers/icons/knife-kill.webp',
    description: 'The ultimate disrespect.',
  }],
  [50303, {
    id: 50303,
    name: 'Wallbang (Foil)',
    collection: 'Skill Icons',
    rarity: 'classified',
    texturePath: 'stickers/icons/wallbang-foil.png',
    holographic: false,
    foil: true,
    glitter: false,
    thumbnailPath: 'thumbs/stickers/icons/wallbang-foil.webp',
    description: 'Walls can\'t save you.',
  }],
  [50304, {
    id: 50304,
    name: 'GG (Contraband)',
    collection: 'Skill Icons',
    rarity: 'contraband',
    texturePath: 'stickers/icons/gg-contraband.png',
    holographic: true,
    foil: true,
    glitter: true,
    thumbnailPath: 'thumbs/stickers/icons/gg-contraband.webp',
    description: 'The rarest GG you\'ll ever see. All effects enabled.',
  }],
]);
